import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { useCookieConsent } from '@/providers/CookieConsentProvider';
import { CookiePreferences } from '@/lib/cookies';
import { InfoCircledIcon } from '@radix-ui/react-icons';

interface CookieCategoryInfo {
  name: string;
  description: string;
  required?: boolean;
}

type CookieCategoryKey = 'functional' | 'analytics' | 'marketing';

const cookieCategories: Record<CookieCategoryKey, CookieCategoryInfo> = {
  functional: {
    name: 'Functional Cookies',
    description:
      'These cookies are necessary for the website to function properly. They enable basic functions like page navigation and access to secure areas.',
    required: true,
  },
  analytics: {
    name: 'Analytics Cookies',
    description:
      'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
  },
  marketing: {
    name: 'Marketing Cookies',
    description:
      'These cookies are used to track visitors across websites to display ads that are relevant and engaging for the individual user.',
  },
};

export const CookiePreferencesDialog: React.FC = () => {
  const {
    showPreferences,
    preferences: currentPreferences,
    savePreferences,
    closePreferences,
    acceptAll,
    rejectAll,
  } = useCookieConsent();

  const [localPreferences, setLocalPreferences] =
    useState<CookiePreferences>(currentPreferences);

  useEffect(() => {
    setLocalPreferences(currentPreferences);
  }, [currentPreferences]);

  const handleToggle = (category: CookieCategoryKey) => {
    if (category === 'functional') return; // Functional cookies are always required

    setLocalPreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSave = () => {
    savePreferences(localPreferences);
  };

  const handleRejectAll = () => {
    rejectAll();
  };

  const handleAcceptAll = () => {
    acceptAll();
  };

  return (
    <Dialog open={showPreferences} onOpenChange={closePreferences}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cookie Preferences</DialogTitle>
          <DialogDescription>
            Manage your cookie preferences below. You can enable or disable
            different categories of cookies except for functional cookies which
            are necessary for the website to work properly.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {Object.entries(cookieCategories).map(([category, info]) => {
            const categoryKey = category as CookieCategoryKey;
            const isEnabled = localPreferences[categoryKey];
            const isRequired = info.required;

            return (
              <div
                key={category}
                className="flex items-start space-x-4 rounded-lg border p-4"
              >
                <Switch
                  id={category}
                  checked={isEnabled}
                  onCheckedChange={() => handleToggle(categoryKey)}
                  disabled={isRequired}
                  className="mt-0.5"
                />
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <Label
                      htmlFor={category}
                      className="text-base font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {info.name}
                    </Label>
                    {isRequired && (
                      <span className="rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                        Required
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {info.description}
                  </p>
                </div>
              </div>
            );
          })}

          <div className="rounded-lg bg-muted/50 p-4">
            <div className="flex items-start gap-2">
              <InfoCircledIcon className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div className="text-sm text-muted-foreground">
                <p>
                  For more information about how we use cookies and process your
                  data, please read our{' '}
                  <a
                    href="/privacy"
                    className="font-medium text-primary hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={handleRejectAll}
              className="w-full sm:w-auto"
            >
              Reject All
            </Button>
            <Button
              variant="outline"
              onClick={handleAcceptAll}
              className="w-full sm:w-auto"
            >
              Accept All
            </Button>
          </div>
          <Button onClick={handleSave} className="w-full sm:w-auto">
            Save Preferences
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
